
import AppHeader from "@/components/AppHeader.vue";
import { useResult } from "@vue/apollo-composable";
import { defineComponent, ref, watch, computed } from "vue";
import { useRouter } from "vue-router";
import useError from "@/composables/useError";
import useUser from "@/composables/useUser";
import {
  BasicOrganizationType,
  useGetOrganisationsQuery,
} from "@/graphql/types";
import usePluralFilter from "@/composables/usePluralFilter";
import Page from "@/components/Page.vue";
import gql from "graphql-tag";
import Error from "@/components/Error.vue";
import { FilterMatchMode } from "primevue/api";
import OrganizationModal from "@/components/modals/OrganizationModal.vue";
import VDModal from "@/components/modals/VDModal";
import useExportAllProfilesToExcel from "@/composables/useExportAllProfilesToExcel";

// Query
gql`
  query getOrganisations {
    organizations {
      count
      items {
        ...BasicOrganizationParts
      }
    }
  }
`;

export default defineComponent({
  name: "organizations",
  components: { AppHeader, Page, Error, OrganizationModal },
  setup() {
    const OrganizationFilterSessionKey = "organizations-filter";

    const { user } = useUser();

    // Role-based computed properties
    const canCreateOrganizations = computed(
      () => user.value?.isSuperAdmin || false
    );

    const canExportUserProfiles = computed(
      () => user.value?.isSuperAdmin || user.value?.isApplicationAdmin || false
    );

    const organizationModal = ref<VDModal<BasicOrganizationType | null> | null>(
      null
    );

    const router = useRouter();

    const { result, loading, error } = useGetOrganisationsQuery();
    function onRowSelect(data: BasicOrganizationType) {
      router.push({
        name: "organization",
        params: { organizationId: data?.organizationId ?? "" },
        query: {
          title: data.name,
        },
      });
    }

    const filteredCount = ref(0);

    // Initialize searchText from query params or sessionStorage
    const initialSearch =
      sessionStorage.getItem(OrganizationFilterSessionKey) || "";
    const searchText = ref(initialSearch);
    const filters = useFilters(initialSearch);

    // Watch searchText with custom logic and persistence
    watch(searchText, (newValue) => {
      // Trim whitespace and handle empty strings
      const trimmedValue = newValue ? newValue.trim() : "";

      // Update filter value
      if (trimmedValue === "") {
        filters.value.global.value = null;
        sessionStorage.removeItem(OrganizationFilterSessionKey);
      } else {
        // You can add custom logic here, like minimum length
        if (trimmedValue.length >= 1) {
          filters.value.global.value = trimmedValue;
          sessionStorage.setItem(OrganizationFilterSessionKey, trimmedValue);
        }
      }
    });

    const onFilter = (event: any) => {
      filteredCount.value = event.filteredValue?.length || 0;
    };

    const {
      exportAll,
      isExporting,
      exportedCount,
      totalCount,
      batchFrom,
      finishedCount,
    } = useExportAllProfilesToExcel();

    return {
      user,
      canCreateOrganizations,
      canExportUserProfiles,
      organizationModal,
      organizations: useResult(result),
      error: useError(error).value || undefined,
      loading,
      result,
      filters,
      onRowSelect,
      usePluralFilter,
      searchText,
      exportAll,
      isExporting,
      exportedCount,
      totalCount,
      batchFrom,
      finishedCount,
      onFilter,
      filteredCount,
    };
  },
});

function useFilters(initialValue = "") {
  return ref({
    global: {
      value: initialValue || null,
      matchMode: FilterMatchMode.CONTAINS,
    },
  });
}
